const DEFAULT_ENVIRONMENT_SHORT_CODE = "local";

const getRuntimeEnvironment = (): Record<string, string> => {
  return (globalThis as typeof globalThis & { __ENV__?: Record<string, string> }).__ENV__ ?? {};
};

export const getExternalRegisterImportEnvironmentShortCode = (): string => {
  const rawCode = import.meta.env.VITE_ENVIRONMENT_SHORT_CODE
    || import.meta.env.VITE_ENV
    || getRuntimeEnvironment().ENV
    || DEFAULT_ENVIRONMENT_SHORT_CODE;

  return String(rawCode)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    || DEFAULT_ENVIRONMENT_SHORT_CODE;
};

export const buildExternalRegisterImportStorageKey = (suffix: string): string => {
  return `trier-${getExternalRegisterImportEnvironmentShortCode()}:${suffix}`;
};

export type BrowserStorageKind = "local" | "session";

export const getBrowserStorage = (kind: BrowserStorageKind): Storage | null => {
  try {
    return kind === "local" ? localStorage : sessionStorage;
  } catch (err) {
    return null;
  }
};

export const safeStorageGetItem = (storage: Storage | null, key: string): string | null => {
  try {
    return storage?.getItem(key) ?? null;
  } catch (err) {
    return null;
  }
};

export const safeStorageSetItem = (storage: Storage | null, key: string, value: string): boolean => {
  try {
    storage?.setItem(key, value);
    return Boolean(storage);
  } catch (err) {
    return false;
  }
};

export const safeStorageRemoveItem = (storage: Storage | null, key: string): void => {
  try {
    storage?.removeItem(key);
  } catch (err) {
    // Ignore blocked storage cleanup.
  }
};

export const safeStorageRemoveItems = (storage: Storage | null, keys: string[]): void => {
  keys.forEach((key) => safeStorageRemoveItem(storage, key));
};

export const safeJsonParse = <TValue>(value: string | null): TValue | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as TValue;
  } catch (err) {
    return null;
  }
};

export const safeJsonSetItem = (storage: Storage | null, key: string, value: unknown): boolean => {
  return safeStorageSetItem(storage, key, JSON.stringify(value));
};
